import {
  useWalletConnect,
  useAppKitBtcBridge,
} from "@babylonlabs-io/wallet-connector";
import { Card, Container } from "@babylonlabs-io/core-ui";
import { useEffect, useState } from "react";

import bitcoinLogo from "@/ui/common/assets/bitcoin-logo.svg";
import { useHealthCheck } from "@/ui/common/hooks/useHealthCheck";

import { Activity } from "./components/Activity/Activity";
import { FAQ } from "./components/FAQ/FAQ";
import { MultistakingFormWrapper } from "./components/Multistaking/MultistakingForm/MultistakingFormWrapper";
import { Stats } from "./components/Stats/Stats";
import { Tabs } from "./components/Tabs";

const Home = () => {
  const [activeTab, setActiveTab] = useState("stake");

  const { connected } = useWalletConnect();
  const { isGeoBlocked, isLoading } = useHealthCheck();
  const isConnected = connected && !isGeoBlocked && !isLoading;

  // Bridge AppKit BTC connection state with babylon-wallet-connector
  useAppKitBtcBridge();

  // Reset tab to "stake" when wallet disconnects
  useEffect(() => {
    if (!connected) {
      setActiveTab("stake");
    }
  }, [connected]);

  const tabItems = [
    {
      id: "stake",
      label: "Stake",
      content: <MultistakingFormWrapper />,
    },
    ...(isConnected
      ? [
          {
            id: "activity",
            label: "Activity",
            content: <Activity />,
          },
        ]
      : []),
    {
      id: "faqs",
      label: "FAQs",
      content: <FAQ variant="btc" />,
    },
  ];

  return (
    <main className="snowfall-dashboard flex-1 px-4 pb-12 md:px-8 md:pb-20">
      <section className="snowfall-hero mx-auto max-w-[1180px]">
        <div className="snowfall-hero-content">
          <span className="snowfall-eyebrow">
            <span aria-hidden="true" /> Self-custodial Bitcoin staking
          </span>
          <h1>
            Put your Bitcoin to work. <strong>Keep it yours.</strong>
          </h1>
          <p>
            Stake BTC directly with Babylon through a secure experience built
            and operated by Snow Fall. Track positions, rewards and providers
            from one dashboard.
          </p>
          <div className="snowfall-hero-points" aria-label="Key benefits">
            <span>Native BTC</span>
            <span>No bridging</span>
            <span>Transparent rewards</span>
          </div>
        </div>

        <div className="snowfall-hero-visual" aria-hidden="true">
          <div className="snowfall-bitcoin-orbit">
            <img src={bitcoinLogo} alt="" className="snowfall-bitcoin-mark" />
          </div>
          <span className="snowfall-network-chip">Secured by Babylon</span>
        </div>
      </section>

      <Card className="snowfall-dashboard-panel container mx-auto mt-6 flex max-w-[1180px] flex-col border-0 px-4 py-6 md:mt-8 md:p-10">
        <Container className="flex max-w-none flex-1 flex-col gap-10 px-0 md:px-0">
          <Stats />

          <section className="snowfall-workspace mx-auto w-full max-w-[780px]">
            <div className="snowfall-workspace-heading">
              <div>
                <span className="snowfall-section-kicker">Staking console</span>
                <h2>Manage your Bitcoin position</h2>
              </div>
              <span className="snowfall-live-status">
                <span aria-hidden="true" /> Network live
              </span>
            </div>

            <Tabs
              className="snowfall-tabs"
              items={tabItems}
              defaultActiveTab="stake"
              activeTab={activeTab}
              onTabChange={setActiveTab}
              keepMounted
            />
          </section>
        </Container>
      </Card>

      <section className="snowfall-trust-row mx-auto max-w-[1180px]">
        <div>
          <strong>Self-custodial</strong>
          <span>Your private keys remain in your wallet.</span>
        </div>
        <div>
          <strong>Native Bitcoin</strong>
          <span>Stake without wrapping or bridging your BTC.</span>
        </div>
        <div>
          <strong>One dashboard</strong>
          <span>Monitor providers, positions and rewards.</span>
        </div>
      </section>
    </main>
  );
};

export default Home;
